import React, { useState, useRef, useEffect } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useFollowUpsColumns, useFollowUpsFormFields,useFollowUpCardView } from '../hooks';
import {
  ReloadOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import { message, Button, Tooltip } from 'antd';
import { useNavigation, useInvalidate } from '@refinedev/core';
import dayjs from 'dayjs';

export const FollowUpsList: React.FC = () => {
  const { push } = useNavigation();
  const [rescheduleData, setRescheduleData] = useState<any>(null);
  const [autoOpenCreate, setAutoOpenCreate] = useState(false);
  const [rescheduleId, setRescheduleId] = useState<number>(0);
  const [formInstanceKey, setFormInstanceKey] = useState<string>('');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Reset reschedule state when component mounts or unmounts
  useEffect(() => {
    return () => {
      setRescheduleData(null);
      setAutoOpenCreate(false);
      setRescheduleId(0);
      setFormInstanceKey('');
      setSelectedRecord(null);
    };
  }, []);

  // Additional cleanup effect to ensure reschedule data is cleared when needed
  useEffect(() => {
    if (rescheduleData && !autoOpenCreate) {
      // If we have reschedule data but the form is not open, clear it after a delay
      const timer = setTimeout(() => {
        setRescheduleData(null);
        setRescheduleId(0);
        setFormInstanceKey('');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [rescheduleData, autoOpenCreate]);

  // Function to handle redirect to different types
  const handleTypeRedirect = (
    type: string,
    referenceId: string | number,
    record?: any
  ) => {
    if (!referenceId) {
      message.warning('No reference ID available for this follow-up');
      return;
    }

    const baseUrl = window.location.origin;

    switch (type) {
      case 'PATIENT':
        // Open patient details page in new tab
        window.open(`${baseUrl}/patients/${referenceId}`, '_blank');
        break;
      case 'APPOINTMENT':
        // Store the appointment ID and open appointments page in new tab
        localStorage.setItem('autoEditAppointmentId', referenceId.toString());
        localStorage.setItem('autoEditTimestamp', Date.now().toString());
        window.open(`${baseUrl}/appointments`, '_blank');
        message.info(
          'Opening appointments page in new tab. The edit form will open automatically.'
        );
        break;
      case 'LEAD':
        // Store the lead ID and open leads page in new tab
        localStorage.setItem('autoEditLeadId', referenceId.toString());
        localStorage.setItem('autoEditTimestamp', Date.now().toString());
        window.open(`${baseUrl}/leads`, '_blank');
        message.info(
          'Opening leads page in new tab. The edit form will open automatically.'
        );
        break;
      case 'FEEDBACK':
        // For treatments, we need to navigate to the patient details page first
        // The reference_id is the treatment ID, but we need the patient_id to navigate
        if (record?.patient_id) {
          // Store the treatment ID and open patient details page with treatment tab in new tab
          localStorage.setItem('autoEditTreatmentId', referenceId.toString());
          localStorage.setItem('autoEditTimestamp', Date.now().toString());
          window.open(
            `${baseUrl}/patients/${record.patient_id}?tab=treatments`,
            '_blank'
          );
          message.info(
            'Opening patient details page in new tab. The treatment edit form will open automatically.'
          );
        } else {
          message.warning(
            'Patient ID not available for this treatment follow-up'
          );
        }
        break;
      default:
        message.warning('Unknown follow-up type');
    }
  };

  // Function to render redirect button based on type
  const renderRedirectButton = (
    type: string,
    referenceId: string | number,
    record?: any
  ) => {
    if (!referenceId) return null;

    const buttonConfig = {
      PATIENT: {
        icon: <UserOutlined />,
        text: 'GO TO PATIENT',
        color: '#52c41a',
        tooltip: `Click to edit patient details (ID: ${referenceId})`,
      },
      APPOINTMENT: {
        icon: <CalendarOutlined />,
        text: 'GO TO APPOINTMENT',
        color: '#1890ff',
        tooltip: `Click to edit appointment details (ID: ${referenceId})`,
      },
      LEAD: {
        icon: <FileTextOutlined />,
        text: 'GO TO LEAD',
        color: '#fa8c16',
        tooltip: `Click to edit lead details (ID: ${referenceId})`,
      },
      FEEDBACK: {
        icon: <MedicineBoxOutlined />,
        text: 'GO TO TREATMENT',
        color: '#722ed1',
        tooltip: `Click to edit treatment details (ID: ${referenceId})`,
      },
    };

    const config = buttonConfig[type as keyof typeof buttonConfig];
    if (!config) return null;

    return (
      <Tooltip title={config.tooltip} placement="top">
        <Button
          type="primary"
          icon={config.icon}
          size="small"
          style={{
            backgroundColor: config.color,
            borderColor: config.color,
            marginLeft: '8px',
            fontWeight: 500,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
          onClick={e => {
            e.stopPropagation();
            handleTypeRedirect(type, referenceId, record);
          }}
        >
          {config.text}
        </Button>
      </Tooltip>
    );
  };

  // Create custom title component with redirect button
  const createCustomTitle = () => {
    if (!selectedRecord) return 'Edit Task';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '4px 0',
        }}
      >
        <span
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#262626',
          }}
        >
          Edit Task
        </span>
        {renderRedirectButton(
          selectedRecord.type,
          selectedRecord.reference_id,
          selectedRecord
        )}
      </div>
    );
  };

  const onRescheduleClick = (record: any) => {
    if (!record) {
      console.warn('No record provided for rescheduling');
      return;
    }

    // Check if reschedule is allowed based on status
    if (record.status === 'PENDING') {
      message.warning('Cannot reschedule pending follow-ups');
      return;
    }

    if (record.status === 'CANCELLED') {
      message.warning('Cannot reschedule cancelled follow-ups');
      return;
    }

    // Prevent rescheduling if there's already reschedule data in progress
    if (rescheduleData) {
      message.warning(
        'Please complete or cancel the current reschedule operation first.'
      );
      return;
    }

    try {
      // Generate a new reschedule ID, timestamp, and unique form instance key
      const newRescheduleId = rescheduleId + 1;
      const timestamp = Date.now();
      const uniqueFormKey = `form_${timestamp}_${newRescheduleId}_${Math.random().toString(36).substr(2, 9)}`;

      setRescheduleId(newRescheduleId);
      setFormInstanceKey(uniqueFormKey);

      // Prepare the data to reschedule, copying relevant fields and incrementing attempt count
      const rescheduleData = {
        rescheduleId: newRescheduleId, // Add unique identifier
        timestamp: timestamp, // Add timestamp for additional uniqueness
        formInstanceKey: uniqueFormKey, // Add unique form instance key
        type: record.type,
        reference_id: record.reference_id,
        patient_name: record.patient_name,
        patient_phone: record.patient_phone.substring(2),
        follow_up_date: dayjs()
          .add(1, 'day')
          .hour(9)
          .minute(0)
          .format('YYYY-MM-DD HH:mm:ss'),
        status: 'PENDING',
        outcome: null,
        attempt_count: +record.attempt_count + +1,
        remark: `Rescheduled from follow-up #${record.id}. Previous attempt: ${record?.attempt_count || 0}`,
      };

      // Set the reschedule data and trigger auto-open
      setRescheduleData(rescheduleData);
      setAutoOpenCreate(true);

      message.success('Follow-up rescheduled successfully!');
    } catch (error) {
      console.error('Error rescheduling follow-up data:', error);
      message.error('Failed to reschedule follow-up. Please try again.');
    }
  };

  const handleCreateSuccess = () => {
    // Reset the reschedule data and auto-open state after successful creation
    setRescheduleData(null);
    setAutoOpenCreate(false);
    // Also reset the reschedule ID and form instance key to ensure complete cleanup
    setRescheduleId(0);
    setFormInstanceKey('');
  };

  const handleCreateDrawerClose = () => {
    // Reset the reschedule data and auto-open state when drawer is closed
    setRescheduleData(null);
    setAutoOpenCreate(false);
    // Also reset the reschedule ID and form instance key to ensure complete cleanup
    setRescheduleId(0);
    setFormInstanceKey('');
  };

  const columns = useFollowUpsColumns({ showCreatedAt: true }); // You can set this to false to hide the column
  const { FollowUpCardView } = useFollowUpCardView();
  
  // Get form fields for both create and edit modes
  const createFormFields = useFollowUpsFormFields(); // No record for create mode
  const editFormFields = useFollowUpsFormFields(selectedRecord); // Pass selected record for edit mode



  // Custom actions for the table
  const customActions = [
    {
      key: 'reschedule',
      icon: <ReloadOutlined />,
      label: '',
      onClick: onRescheduleClick,
      type: 'default' as const,
      tooltip: 'Reschedule Follow-up',
    },
  ];

  return (
    <div className="flex-1 overflow-hidden">
      <SmartDataTable
        key={formInstanceKey || 'followups-table'}
        api={{
          resource: 'tasks',
        }}
        title="Tasks"
        columns={columns}
        mobileCardComponent={FollowUpCardView}
        search={{
          enabled: true,
          fields: [
            'id',
            'type',
            'reference_id',
            'patient_name',
            'patient_phone',
          ],
          placeholder:
            'Search by ID, type, reference ID, patient name, or phone...',
          debounceMs: 500,
        }}
        createForm={{
          fields: createFormFields as any,
          title: 'Create Task',
          submitText: 'Create Task',
          initialValues: rescheduleData
            ? {
                ...rescheduleData,
                _rescheduleTimestamp: rescheduleData.timestamp, // Force form refresh
                _rescheduleId: rescheduleData.rescheduleId, // Additional uniqueness
                _formInstanceKey: rescheduleData.formInstanceKey, // Form instance uniqueness
              }
            : {},
        }}
        editForm={{
          fields: editFormFields as any,
          title: 'Edit Task',
          submitText: 'Save Changes',
          customTitle: createCustomTitle(),
        }}
        formDisplayMode="drawer"
        drawerWidth={1200}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        pageSize={10}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 240px)',
          },
        }}
        rowKey="id"
        customActions={customActions}
        autoOpenCreate={autoOpenCreate}
        onCreateSuccess={handleCreateSuccess}
        onCreateDrawerClose={handleCreateDrawerClose}
        onEditClick={(record) => setSelectedRecord(record)}
        // Chat configuration
        enableChat={true}
        chatType={
          selectedRecord?.type === 'APPOINTMENT'
            ? 'A_FOLLOW_UP'
            : selectedRecord?.type === 'PATIENT'
              ? 'P_FOLLOW_UP'
              : selectedRecord?.type === 'FEEDBACK'
                ? 'FEEDBACK'
                : selectedRecord?.type === 'LEAD'
                  ? 'L_FOLLOW_UP'
                  : selectedRecord?.type
        }
        chatTitle={
          selectedRecord?.type === 'LEAD'
            ? 'Lead Messages'
            : selectedRecord?.patient_id
              ? 'Patient Messages'
              : 'Appointment Message'
        }
        chatPlaceholder={
          selectedRecord?.type === 'LEAD'
            ? 'Add a note for lead...'
            : selectedRecord?.patient_id
              ? 'Add a note for patient...'
              : 'Add a note for appointment...'
        }
        chatRelativeId={
          selectedRecord?.type === 'LEAD'
            ? selectedRecord?.reference_id
              ? parseInt(selectedRecord.reference_id)
              : undefined
            : selectedRecord?.type === 'APPOINTMENT'
              ? selectedRecord?.patient_id
                ? selectedRecord.patient_id
                : selectedRecord?.reference_id
                  ? parseInt(selectedRecord.reference_id)
                  : undefined
              : selectedRecord?.patient_id ||
                (selectedRecord?.reference_id
                  ? parseInt(selectedRecord.reference_id)
                  : undefined)
        }
        chatPatientId={
          selectedRecord?.type === 'LEAD'
            ? undefined
            : selectedRecord?.patient_id ||
              (selectedRecord?.reference_id
                ? parseInt(selectedRecord.reference_id)
                : undefined)
        }
        isintialScroll={true}
      />
    </div>
  );
};
